// Driver chat — channel list + thread + composer in one page.
// Drivers see only channels they're a member of. Dispatchers/admins see all
// and can broadcast. SMS push is opt-in per message (default on).

import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile, MobilePage, StickyAction, Icon } from '../../mobile';

function fmtTime(s) {
  if (!s) return '';
  const d = new Date(s + (s.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleString();
}

export default function DriverChatPage() {
  const { user, isStationAccount, isAtLeastManager } = useAuth();
  const isMobile = useIsMobile();
  const canPost = isStationAccount || isAtLeastManager || user?.role === 'dispatcher' || user?.position_name === 'Dispatcher';
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sendSms, setSendSms] = useState(true);
  const [preview, setPreview] = useState(null);
  const [unread, setUnread] = useState({});
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.listChatChannels().then(c => {
      setChannels(c);
      if (c[0]) setActiveId(c[0].id);
    });
    api.chatUnreadCounts().then(setUnread).catch(() => {});
    const id = setInterval(() => {
      api.chatUnreadCounts().then(setUnread).catch(() => {});
    }, 30 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!activeId) return;
    let cancel = false;
    function load() {
      api.listChatMessages(activeId).then(m => {
        if (cancel) return;
        setMessages(m);
        // Mark read
        if (m.length) api.markChatRead(activeId, m[m.length - 1].id).catch(() => {});
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 0);
      });
    }
    load();
    if (canPost) api.chatChannelPreview(activeId).then(setPreview).catch(() => setPreview(null));
    const id = setInterval(load, 15 * 1000);
    return () => { cancel = true; clearInterval(id); };
  }, [activeId, canPost]);

  async function send() {
    if (!body.trim() || !activeId) return;
    setBusy(true);
    try {
      await api.postChatMessage(activeId, body.trim(), sendSms);
      setBody('');
      const m = await api.listChatMessages(activeId);
      setMessages(m);
      if (m.length) api.markChatRead(activeId, m[m.length - 1].id).catch(() => {});
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  // ── Phone ───────────────────────────────────────────────────────────
  // Channels are chips, messages are bubbles, and for the people who can
  // post the composer is pinned above the tab bar with a real Send button.
  // Drivers read; the page is the thread, nothing else.
  if (isMobile) {
    const active = channels.find(c => c.id === activeId);
    return (
      <MobilePage title="Driver Comms" subtitle={active ? active.name : undefined} style={{ paddingBottom: canPost ? 96 : 0 }}>
        <div className="m-chips">
          {channels.map(c => (
            <button key={c.id} type="button" className={`m-chip ${activeId === c.id ? 'active' : ''}`} onClick={() => setActiveId(c.id)}>
              {c.name}{unread[c.id] > 0 && <span className="m-chip-count">{unread[c.id]}</span>}
            </button>
          ))}
        </div>
        {channels.length === 0 && <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>No channels available.</div>}
        {channels.length > 0 && messages.length === 0 && <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>No messages yet.</div>}
        <div className="m-list" style={{ gap: 8 }}>
          {messages.map(m => {
            const mine = m.sender_user_id === user?.id || m.acting_user_id === user?.id;
            return (
              <div key={m.id} className="m-card" style={{ padding: '10px 14px', borderRadius: 14, background: mine ? '#eff6ff' : '#fff', borderColor: mine ? '#bfdbfe' : undefined }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{m.acting_full_name || m.sender_name}</span>
                  <span style={{ color: 'var(--text-faint)' }}>{fmtTime(m.created_at)}</span>
                  {m.send_sms ? <span className="m-pill" style={{ height: 18, fontSize: 10, background: '#dbeafe', color: '#1e40af' }}>SMS</span> : null}
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.4, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{m.body}</div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        {canPost && activeId && (
          <StickyAction>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea className="m-textarea" value={body} onChange={e => setBody(e.target.value)} placeholder="Message drivers…" rows={1}
                  style={{ minHeight: 48, maxHeight: 120, padding: '12px 14px', flex: 1 }} />
                <button type="button" className="m-btn" onClick={send} disabled={!body.trim() || busy} aria-label="Send"
                  style={{ width: 52, height: 52, padding: 0, background: 'var(--brand-accent)', color: '#fff', flexShrink: 0 }}>
                  {busy ? '…' : <Icon name="nav" size={22} color="#fff" />}
                </button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', minHeight: 28 }}>
                <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} style={{ width: 20, height: 20, accentColor: 'var(--brand-gold)' }} />
                Also text {sendSms && preview ? `${preview.with_phone} of ${preview.total} drivers` : 'the drivers'}
              </label>
            </div>
          </StickyAction>
        )}
      </MobilePage>
    );
  }

  return (
    <div className="page driver-chat-page">
      <div className="page-header"><h2>Driver Comms</h2></div>
      <div className="driver-chat-layout">
        <aside className="driver-chat-channels">
          {channels.length === 0 && <div className="empty muted">No channels available.</div>}
          {channels.map(c => (
            <button
              key={c.id}
              className={`driver-chat-channel ${activeId === c.id ? 'is-active' : ''}`}
              onClick={() => setActiveId(c.id)}
            >
              <span>{c.name}</span>
              {unread[c.id] > 0 && <span className="driver-chat-unread">{unread[c.id]}</span>}
            </button>
          ))}
        </aside>
        <section className="driver-chat-thread">
          <div className="driver-chat-messages">
            {messages.length === 0 && <div className="empty muted">No messages yet.</div>}
            {messages.map(m => (
              <div key={m.id} className="driver-chat-msg">
                <div className="driver-chat-msg-header">
                  <span className="driver-chat-msg-name">{m.acting_full_name || m.sender_name}</span>
                  <span className="driver-chat-msg-time">{fmtTime(m.created_at)}</span>
                  {m.send_sms ? <span className="driver-chat-sms-pill">SMS</span> : null}
                </div>
                <div className="driver-chat-msg-body">{m.body}</div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          {canPost && activeId && (
            <div className="driver-chat-composer">
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Message drivers…"
                rows={2}
              />
              <div className="driver-chat-composer-actions">
                <label className="driver-chat-sms-toggle">
                  <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} />
                  Also send SMS
                  {sendSms && preview && (
                    <span className="muted"> · {preview.with_phone}/{preview.total} drivers</span>
                  )}
                </label>
                <button className="btn-primary" onClick={send} disabled={!body.trim() || busy}>
                  {busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
